/**
 * /login — email + password form on AuthLayout.
 *
 * - RHF + zodResolver(LoginRequestSchema) for client-side validation.
 * - Submit calls the useLogin mutation; on 401 we toast a generic
 *   "wrong email or password" (we deliberately don't differentiate so the
 *   page can't be used as an account-existence oracle — Q22 also kills
 *   any "Forgot password?" link).
 * - 429 toasts are emitted by the fetcher itself; we don't double-toast.
 * - On success, navigate to "/" — the route table redirects to the
 *   role-based home (Phase 1: /workload stub for everyone).
 *
 * Live-VPS UI review (2026-05-08, defect #2): screen-reader users had no
 * affordance for a wrong-password failure beyond the bottom-right toast
 * (which auto-dismisses). The fix:
 *
 *   - render an inline `role="alert"` region above the submit button so
 *     assistive tech announces the failure synchronously;
 *   - mark BOTH email and password inputs `aria-invalid="true"` so a
 *     row-level cue is exposed too;
 *   - move keyboard focus back to the email input so re-entry is
 *     immediate without grabbing a mouse.
 *
 * The toast stays as a sighted-user nicety; the inline alert is the
 * authoritative a11y signal.
 */
import * as React from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import type { z } from 'zod';

import { LoginRequestSchema } from '@aris/shared';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { PasswordInput } from '@/components/ui/password-input';
import { toast } from '@/components/ui/toast';
import { ApiError } from '@/lib/fetcher';
import { useLogin } from '@/hooks/use-auth';

type FormValues = z.infer<typeof LoginRequestSchema>;

const WRONG_CREDS_MESSAGE = 'Неверный email или пароль';

export function LoginPage(): React.ReactElement {
  const navigate = useNavigate();
  const login = useLogin();
  const form = useForm<FormValues>({
    resolver: zodResolver(LoginRequestSchema),
    defaultValues: { email: '', password: '' },
    mode: 'onSubmit',
  });

  // Ref points at the email <input>; we focus it programmatically on
  // submit-error so users (especially keyboard / SR) get back to the
  // first invalid field without reaching for a mouse.
  const emailInputRef = React.useRef<HTMLInputElement | null>(null);

  // Inline form-level error (rendered as `role="alert"`). Cleared at
  // submit start so a retry immediately removes the previous failure
  // banner — important for screen readers that re-announce on re-render.
  const [formError, setFormError] = React.useState<string | null>(null);

  const onSubmit = form.handleSubmit((values) => {
    setFormError(null);
    // Both inputs lose their invalid flag at the start of every attempt;
    // we re-set it inside onError below if the server rejects again.
    form.clearErrors(['email', 'password']);

    login.mutate(values, {
      onSuccess: () => {
        navigate('/', { replace: true });
      },
      onError: (err) => {
        // The fetcher already toasts 429. We add a single generic toast for
        // the auth-failure path; everything else (network, 5xx) surfaces
        // through the fetcher too.
        if (err instanceof ApiError && err.status === 401) {
          setFormError(WRONG_CREDS_MESSAGE);
          // Mark both fields invalid (we deliberately don't tell the user
          // *which* one is wrong — see header re: account-existence
          // oracle). RHF's `setError` is what flips `aria-invalid` on
          // the controlled inputs through the FormField wiring.
          form.setError('email', { type: 'server', message: '' });
          form.setError('password', { type: 'server', message: '' });
          toast(WRONG_CREDS_MESSAGE, { variant: 'error' });
          // Defer focus-return to the next paint — RHF flushes its
          // error state synchronously during onError, but React may
          // still be in the middle of committing inputs (especially
          // under StrictMode). requestAnimationFrame is enough to land
          // after the commit without making the test wait on timers.
          requestAnimationFrame(() => emailInputRef.current?.focus());
          return;
        }
        if (err instanceof ApiError && err.status >= 500) {
          toast('Сервер недоступен, попробуйте позже', { variant: 'error' });
        }
      },
    });
  });

  return (
    <div data-testid="login-page" className="space-y-5">
      <div className="space-y-1">
        <h1 className="text-xl font-semibold text-ink">Вход в АРИС</h1>
        <p className="text-sm text-muted-foreground">Введите email и пароль</p>
      </div>
      <Form {...form}>
        <form onSubmit={onSubmit} className="space-y-4" noValidate>
          {/*
            Live region for the form-level failure. We use `role="alert"`
            (implicit aria-live=assertive + aria-atomic=true) so screen
            readers announce immediately on insert. Hidden when there is
            no error — keeping the node in the tree and merely toggling
            text would make assertive readers re-announce the same
            string on unrelated re-renders.
          */}
          {formError ? (
            <div
              role="alert"
              data-testid="login-form-error"
              className="rounded-sm border border-danger bg-danger-wash px-3 py-2 text-sm text-accent-ink"
            >
              {formError}
            </div>
          ) : null}
          {/*
            FormControl already wires `aria-invalid={Boolean(error)}` on
            its Slot child (see components/ui/form.tsx) so calling
            `form.setError('email'/'password', ...)` from onError is
            sufficient — both inputs flip to aria-invalid="true" on a
            401 without per-input wiring here.
          */}
          <FormField
            name="email"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Email</FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    autoComplete="email"
                    autoFocus
                    placeholder="user@example.com"
                    {...field}
                    ref={(node) => {
                      // Forward both react-hook-form's ref AND our local
                      // ref — RHF needs the input registered, and we
                      // need to call .focus() from onError above.
                      field.ref(node);
                      emailInputRef.current = node;
                    }}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <FormField
            name="password"
            control={form.control}
            render={({ field }) => (
              <FormItem>
                <FormLabel>Пароль</FormLabel>
                <FormControl>
                  <PasswordInput autoComplete="current-password" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={login.isPending}
            data-testid="login-submit"
          >
            {login.isPending ? 'Вход…' : 'Войти'}
          </Button>
        </form>
      </Form>
    </div>
  );
}
